import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "@/lib/api";
import {
  ArrowLeft, Loader2, Shield, CheckCircle2, XCircle, Lock,
} from "lucide-react";

/*  Day 7 verdict page. Three suspects, one guess, one answer.
    No peeking: the answer only renders after a guess is submitted.
*/

const SUSPECTS = [
  { id: "juno",   name: "Juno Pike",       hint: "College roommate · co-signed the loan · lied about the kitchen" },
  { id: "rhett",  name: "Rhett Calloway",  hint: "Business partner · losing his gallery stake · alone in the drawing room" },
  { id: "sage",   name: "Sage Durand",     hint: "Half-sister · just re-added to the trust · lied about the kitchen" },
];

const WhodunitVerdict = () => {
  const { token } = useParams();
  const [clue, setClue] = useState(null);
  const [picked, setPicked] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);  // { correct, answer, already_submitted }
  const [err, setErr] = useState("");

  useEffect(() => {
    api.get(`/whodunit-week/clue/${token}/7`)
      .then((r) => setClue(r.data))
      .catch((e) => {
        if (e?.response?.status === 403) {
          setErr("Day 7 hasn't arrived for you yet. Watch your inbox.");
        } else {
          setErr("Couldn't load Day 7. Your token may be invalid.");
        }
      });
  }, [token]);

  const submit = async () => {
    if (!picked) return;
    setSubmitting(true); setErr("");
    try {
      const { data } = await api.post(`/whodunit-week/guess/${token}`, {
        suspect_id: picked,
      });
      setResult(data);
    } catch (e) {
      setErr(e?.response?.data?.detail || "Submission failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#050403] text-[#f4f0ec] pb-24"
      data-testid="whodunit-verdict-page">
      <div className="border-b border-[#2d2424] bg-[#0a0908]">
        <div className="max-w-3xl mx-auto px-5 py-4 flex items-center justify-between">
          <Link to="/whodunit-week"
            className="text-xs text-[#a39797] hover:text-[#f4f0ec] flex items-center gap-1.5"
            data-testid="wv-back">
            <ArrowLeft size={14} /> Whodunit Week
          </Link>
          <div className="text-[10px] tracking-[0.3em] text-[#d4af37]">DAY 7 · THE VERDICT</div>
          <div className="w-14" />
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-5 pt-10">
        {err && (
          <div className="card-noir p-6 text-center" data-testid="wv-error">
            <Lock size={32} className="text-[#5a5050] mx-auto mb-3" />
            <div className="text-[#f87171]">{err}</div>
          </div>
        )}

        {!err && !clue && (
          <div className="text-center py-16 text-[#5a5050]">
            <Loader2 size={16} className="inline animate-spin mr-2" />
            Loading the case file…
          </div>
        )}

        {clue && !result && (
          <>
            <h1 className="font-display text-5xl text-[#f4f0ec] leading-tight">
              {clue.title}
            </h1>
            <div className="card-noir p-6 mt-6 bg-black/60"
              dangerouslySetInnerHTML={{ __html: clue.body_html }} />

            <div className="mt-8">
              <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-3">
                CHOOSE ONE · ONE SHOT · NO REDO
              </div>
              <div className="space-y-2" data-testid="wv-suspects">
                {SUSPECTS.map((s) => (
                  <label key={s.id}
                    className={`block card-noir p-4 cursor-pointer transition-colors ${
                      picked === s.id ? "border-[#d4af37] bg-[#d4af37]/5" : "hover:border-[#5a5050]"
                    }`}
                    data-testid={`wv-suspect-${s.id}`}>
                    <input type="radio" name="suspect" value={s.id}
                      checked={picked === s.id}
                      onChange={(e) => setPicked(e.target.value)}
                      className="sr-only" />
                    <div className="flex items-center gap-3">
                      <div className={`h-4 w-4 rounded-full border-2 ${
                        picked === s.id ? "border-[#d4af37] bg-[#d4af37]" : "border-[#5a5050]"
                      }`} />
                      <div className="flex-1">
                        <div className="text-[#f4f0ec] font-bold">{s.name}</div>
                        <div className="text-xs text-[#a39797]">{s.hint}</div>
                      </div>
                    </div>
                  </label>
                ))}
              </div>
              <button
                onClick={submit}
                disabled={!picked || submitting}
                className="btn-primary w-full mt-6 justify-center disabled:opacity-50"
                data-testid="wv-submit">
                {submitting ? <Loader2 size={14} className="animate-spin" /> : <Shield size={14} />}
                Lock in my verdict
              </button>
            </div>
          </>
        )}

        {result && (
          <div data-testid="wv-result">
            <div className="card-noir p-8 bg-black/70 text-center">
              {result.correct ? (
                <>
                  <CheckCircle2 size={56} className="text-[#4ade80] mx-auto mb-3" />
                  <div className="text-[10px] tracking-[0.3em] text-[#4ade80] mb-2">CORRECT · CLUE HUNTER UNLOCKED</div>
                  <div className="font-display text-4xl text-[#f4f0ec]">You got it.</div>
                </>
              ) : (
                <>
                  <XCircle size={56} className="text-[#f87171] mx-auto mb-3" />
                  <div className="text-[10px] tracking-[0.3em] text-[#f87171] mb-2">INCORRECT</div>
                  <div className="font-display text-4xl text-[#f4f0ec]">Not this time.</div>
                </>
              )}
              {result.already_submitted && (
                <div className="text-[10px] tracking-[0.3em] text-[#5a5050] mt-3">
                  (Verdict locked earlier)
                </div>
              )}
            </div>
            <div className="card-noir p-6 mt-6 bg-black/60">
              <div className="text-[10px] tracking-[0.3em] text-[#d4af37] mb-3">THE SOLUTION</div>
              <div className="font-display text-2xl text-[#f4f0ec] mb-3">{result.answer.name}</div>
              <div dangerouslySetInnerHTML={{ __html: result.answer.reveal_body }} />
            </div>
            <div className="text-center mt-6">
              <Link to="/" className="btn-primary" data-testid="wv-home">
                Enter the site →
              </Link>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default WhodunitVerdict;
